"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { CircleButton } from "../custom/circle";
import { LuX } from "react-icons/lu";
import { Input } from "./input";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormFieldValue = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }
  const { watch } = useFormContext();
  return watch(fieldContext.name);
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }

  const fieldState = getFieldState(fieldContext.name, formState);

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

export type FormItemProps = React.HTMLAttributes<HTMLDivElement>;

const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
  ({ className, ...props }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn("space-y-2", className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive font-normal", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = "FormLabel";

const FormInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentPropsWithoutRef<typeof Input>
>(({ className, ...props }, ref) => {
  const { error } = useFormField();

  return (
    <Input
      ref={ref}
      className={cn(error && "border-destructive", className)}
      {...props}
    />
  );
});
FormInput.displayName = "FormInput";

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

const useFormControlProps = () => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();
  return {
    ["id"]: formItemId,
    ["aria-describedby"]: !error
      ? `${formDescriptionId}`
      : `${formDescriptionId} ${formMessageId}`,
    ["aria-invalid"]: !!error,
  };
};

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
FormDescription.displayName = "FormDescription";

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = "FormMessage";

const FormClearMany = React.forwardRef<
  React.ElementRef<typeof CircleButton>,
  Omit<React.ComponentPropsWithoutRef<typeof CircleButton>, "onClick"> & {
    isResettable?: (v: any) => boolean;
    resetTarget?: any;
    fields: string[];
  }
>(({ className, fields, isResettable, resetTarget, ...props }, ref) => {
  const form = useFormContext();
  const isDirty = fields.find((name) => form.formState.dirtyFields[name]);
  const isReset = isResettable
    ? form.watch(fields).find(isResettable)
    : undefined;
  return (
    <CircleButton
      {...props}
      ref={ref}
      className={cn(className, isDirty || isReset ? "visible" : "invisible")}
      onClick={(ev) => {
        ev.preventDefault();
        if (isDirty) {
          fields.map((name) =>
            form.resetField(name, {
              keepDirty: false,
              keepError: false,
              keepTouched: true,
              defaultValue: form.formState.defaultValues?.[name],
            }),
          );
        } else if (isReset) {
          fields.map((name) =>
            form.setValue(name, resetTarget, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: false,
            }),
          );
        }
      }}
    >
      <LuX strokeWidth={isReset && !isDirty ? "3px" : "2px"} />
    </CircleButton>
  );
});
FormControl.displayName = "FormClearMany";

export type FormClearProps = {
  isResettable?: boolean | ((v: any) => boolean);
  resetTarget?: any;
};

const FormClear = React.forwardRef<
  React.ElementRef<typeof CircleButton>,
  Omit<React.ComponentPropsWithoutRef<typeof CircleButton>, "onClick"> & {
    isResettable?: boolean | ((v: any) => boolean);
    resetTarget?: any;
  }
>(({ className, isResettable, resetTarget, ...props }, ref) => {
  const form = useFormContext();
  const field = useFormField();
  const isDirty = form.formState.dirtyFields?.[field.name];
  const isReset = !isResettable
    ? false
    : isResettable === true
      ? true
      : isResettable
        ? isResettable(form.watch(field.name))
        : undefined;
  return (
    <CircleButton
      {...props}
      ref={ref}
      className={cn(className, isDirty || isReset ? "visible" : "invisible")}
      onClick={(ev) => {
        ev.preventDefault();
        if (isDirty) {
          form.resetField(field.name, {
            keepDirty: false,
            keepError: false,
            keepTouched: true,
            defaultValue: form.formState.defaultValues?.[field.name],
          });
        } else if (isReset) {
          form.setValue(field.name, resetTarget, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
        }
      }}
    >
      {isReset && !isDirty ? <LuX /> : <MdOutlineSettingsBackupRestore />}
    </CircleButton>
  );
});
FormControl.displayName = "FormClear";

export {
  useFormFieldValue,
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormInput,
  FormControl,
  useFormControlProps,
  FormDescription,
  FormMessage,
  FormField,
  FormClear,
  FormClearMany,
};
